import { useCallback, useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { getRoundedValueOfPercentage } from '@sg/garnish';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Returns formatted tip items after doing multiple safety checks to guarantee that it is valid.
 */
export const useBagTips = (params: UseBagTipsParams) => {
  const { maxTipAmount, availableTips } = params;
  const { formatMessage, formatNumber } = useIntl();

  const safelyGetTip = useCallback(
    (tipValue: UseBagTipsParams['availableTips'][number]): Tip | undefined => {
      const formatPrice = (value: string | number) =>
        formatNumber(Number(value) / 100, {
          style: 'currency',
          currency: 'USD',
        });

      const hasValidTipValue = typeof tipValue === 'number' || tipValue === 'CUSTOM';

      if (!hasValidTipValue) return;

      // ─── "No Tip" option ─────────────────────────

      if (tipValue === 0) {
        return {
          value: '0',
          amount: 0,
          label: formatMessage(messages.noTip),
          accessibilityHint: formatMessage(messages.noTipA11yHint),
        };
      }

      // ─── "Custom" tip option ─────────────────────

      if (tipValue === 'CUSTOM') {
        return {
          value: 'custom',
          label: formatMessage(messages.custom),
          accessibilityHint: formatMessage(messages.customTipA11yHint),
        };
      }

      // ─── Standard tip option ─────────────────────

      const value = `${tipValue}`;
      const label = `${tipValue}%`;
      const amount = getRoundedValueOfPercentage(tipValue, maxTipAmount);
      const helperText = formatPrice(amount);
      const accessibilityHint = formatMessage(messages.tipA11yHint, {
        tip: label,
        amount: helperText,
      });

      return { value, label, helperText, amount, accessibilityHint };
    },
    [maxTipAmount, formatMessage, formatNumber],
  );

  return useMemo<Tips>(() => {
    const tipsWithExtraDetails = availableTips.map(safelyGetTip);

    return tipsWithExtraDetails.filter(Boolean) as Tips;
  }, [availableTips, safelyGetTip]);
};

// ─── Messages ───────────────────────────────────────────────────────────────

const messages = defineMessages({
  custom: {
    defaultMessage: 'Custom',
    description: 'Bag | Tipping | Custom',
  },
  noTip: {
    defaultMessage: 'No tip',
    description: 'Bag | Tipping | No tip',
  },
  tipA11yHint: {
    defaultMessage: 'Selects "{tip} ({amount})" tip option',
    description: 'Bag | Tipping | Tip a11y hint',
  },
  noTipA11yHint: {
    defaultMessage: 'Selects "No tip" option',
    description: 'Bag | Tipping | No tip a11y hint',
  },
  customTipA11yHint: {
    defaultMessage: 'Selects "Custom" tip option',
    description: 'Bag | Tipping | Custom tip a11y hint',
  },
});

// ─── Types ──────────────────────────────────────────────────────────────────

type UseBagTipsParams = {
  maxTipAmount: number;
  availableTips: ReadonlyArray<number | 'CUSTOM'>;
};

type Tip = Readonly<{
  value: string;
  label: string;
  helperText?: string;
  amount?: number;
  accessibilityHint?: string;
}>;

type Tips = ReadonlyArray<Tip>;
